import {
  FeatureResult,
  GrowthBook,
  InitOptions,
  TrackingCallback,
  useFeatureValue as _useFeatureValue,
  configureCache,
} from '@growthbook/growthbook-react';

import { IS_DEMO, isProd } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { addLogsToSentry } from 'helpers/sentry';

import { appFeatureDefaults } from './AppFeatureDefaults';
import { AppFeatureTypes } from './AppFeatureTypes';
import { userABExperiments } from './UserABExperiments';

export const featureInitOptions: InitOptions = {
  streaming: false,
  timeout: 30000,
};

export const trackingCallback: TrackingCallback = (experiment, result) => {
  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.GROWTHBOOK_EXPERIMENT_STARTED, {
    experimentId: experiment.key,
    variationName: result.name,
    variationId: result.variationId,
  });
  userABExperiments[experiment.key] = result.variationId;
};

export const onFeatureUsage: (featureKey: string, result: FeatureResult) => void = (
  featureKey,
  result
) => {
  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.GROWTHBOOK_FEATURE_USED, {
    featureKey,
    value: result.value,
    source: result.source,
  });
};

configureCache({
  // 30 minutes
  staleTTL: 1000 * 60 * 30,

  // 5 minutes
  maxAge: 1000 * 60 * 5,
});

export const getGrowthBookConfig = () => ({
  apiHost: 'https://experiments.delta.exchange',
  clientKey: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
  enableDevMode: !(isProd || IS_DEMO),
  trackingCallback,
  onFeatureUsage,
});

export const growthBookInstance = new GrowthBook<AppFeatureTypes>(getGrowthBookConfig());

// Initialize GrowthBook
// This is a one-time initialization that will be done when the app starts
growthBookInstance
  .init(featureInitOptions)
  .then(result => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.GROWTHBOOK_INIT, {
      source: result.source,
      error: result?.error?.message,
    });

    if (result.error) {
      addLogsToSentry({}, 'Growthbook Init Failed', {
        extra: {
          error: result.error,
          source: result.source,
        },
      });
    }
  })
  .catch(error => {
    addLogsToSentry({}, 'Growthbook Init Failed', {
      extra: {
        error,
        source: 'error',
      },
    });
  });

export const setGrowthBookUserID = (userId: number) => {
  growthBookInstance.setAttributes({ ...growthBookInstance.getAttributes(), id: userId });
};

export const resetGrowthBookUser = () => {
  growthBookInstance.setAttributes({
    ...growthBookInstance.getAttributes(),
    id: undefined,
  });

  const keys = Object.getOwnPropertyNames(userABExperiments);
  for (let i = 0; i < keys.length; i += 1) {
    delete userABExperiments[keys[i]];
  }
};

export const useGrowthbookFeatureValue = (featureKey: keyof AppFeatureTypes) => {
  return _useFeatureValue(featureKey, appFeatureDefaults[featureKey]);
};

export const getGrowthBookFeatureValue = (featureKey: keyof AppFeatureTypes) => {
  return growthBookInstance.getFeatureValue(featureKey, appFeatureDefaults[featureKey]);
};

// If the component needs to wait till growthbook is initialised, we can use following:

// <FeaturesReady timeout={500} fallback={<LoadingSpinner/>}>
//   <ComponentThatUsesFeatures/>
// </FeaturesReady>
//      or
// const gb = useGrowthBook();
// if (gb.ready) {
//   <ComponentThatUsesFeatures/>
// }
